import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

const Section = styled.section`
  ${tw`mb-8`}
  @media print {
    margin-top: 12px !important;
    page-break-before: auto;
  }
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2`}
`;

const Amendment = styled.article`
  ${tw`mb-4 text-sm leading-relaxed`}
  @media print {
    page-break-inside: avoid;
  }
`;

const Title = styled.h3`
  ${tw`text-base font-semibold mb-1`}
`;

export type AmendmentRecord = {
  id: string;
  sequence: number;
  reason: string;
  authorization: string;
  createdAt?: string | null;
  payload?: Record<string, any> | null;
};

function when(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function describe(record: AmendmentRecord): { title: string; body: string } {
  const payload = record.payload ?? {};
  const date = when(record.createdAt);
  if (record.reason === 'EXTENSION') {
    const to = payload.change?.to?.endAt;
    return {
      title: `Extension · ${date}`,
      body: to ? `Trip end moved to ${to}.` : 'The trip was extended.',
    };
  }
  if (record.reason === 'SHORTENING') {
    const to = payload.change?.to?.endAt;
    return {
      title: `Shortening · ${date}`,
      body: to ? `Trip end moved to ${to}.` : 'The trip was shortened.',
    };
  }
  if (record.reason === 'SWAP') {
    const hostApproved =
      record.authorization === 'HOST_FORCED' || payload.swap?.forcedByHost === true;
    return {
      title: `Car swap · ${date}`,
      body: hostApproved
        ? 'The vehicle was changed, approved by the host.'
        : 'The vehicle was changed.',
    };
  }
  if (record.reason === 'CHECK_IN' || record.reason === 'RETURN') {
    const keys = Array.isArray(payload.condition?.photoKeys) ? payload.condition.photoKeys : [];
    const stage = record.reason === 'RETURN' ? 'Return' : 'Check-in';
    const photos = keys.length ? ` Photos: ${keys.join(', ')}.` : '';
    const reading = [
      payload.condition?.odometer != null ? `Odometer ${payload.condition.odometer}` : '',
      payload.condition?.fuel != null ? `fuel ${payload.condition.fuel}` : '',
    ]
      .filter(Boolean)
      .join(', ');
    return {
      title: `${stage} · ${date}`,
      body: `${reading}${photos}`.trim() || `${stage} recorded.`,
    };
  }
  if (record.reason === 'ADDITIONAL_DRIVER') {
    const name =
      payload.additionalDriver?.name || payload.additionalDriver?.userId || 'An additional driver';
    return { title: `Additional driver · ${date}`, body: `${name} accepted the rental agreement.` };
  }
  return { title: `${record.reason} · ${date}`, body: '' };
}

/** Amendments after the agreement text, each with its date, so a printed copy is complete. */
export const AmendmentsList: React.FC<{ records: AmendmentRecord[] }> = ({ records }) => {
  const amendments = [...records]
    .filter(record => record.sequence !== 1 && record.reason !== 'CHECKOUT')
    .sort((a, b) => a.sequence - b.sequence);
  if (!amendments.length) return null;
  return (
    <Section id="amendments" className="agreement-amendments" data-testid="agreement-amendments">
      <SectionTitle>Amendments</SectionTitle>
      {amendments.map(record => {
        const described = describe(record);
        return (
          <Amendment
            key={record.id}
            className="agreement-amendment"
            data-testid="agreement-amendment"
          >
            <Title>{described.title}</Title>
            {described.body ? <p>{described.body}</p> : null}
          </Amendment>
        );
      })}
    </Section>
  );
};
